// @contexts/Jwt.context.tsx

import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import * as Option from "@utils/Option";

import type { AuthenticationError, JwtResponse } from "@ps-bridge/models/core/dataContracts";
import type { JwtSubscriptionStatus } from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import type { HttpStatusCode } from "@ps-bridge/models/brands/httpStatusCode";
import type { Int } from "@ps-bridge/prim";
import {
    makeJwtSubscriptionStatus,
} from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import { unwrap as unwrapJwtStatus } from "@ps-bridge/models/brands/jwtStatus";
import { useAuthenticationClient } from '@contexts/AuthenticationClient.context';
import { useIoState } from '@utils/useIoState';
import { useIoOptionState } from "@utils/useIoOptionState";
import {
    mkJwtRefreshIntent,
    mkJwtSessionController,
    type JwtSessionController,
} from '@ps-bridge/controllers/jwtSession';
import type { IOts, OptionTs, TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import type { StateHandle, StateRead, StateUpdate } from "@ps-bridge/capabilitiesTypes";
import {
    mkClockNow,
    mkNavigate,
    mkStorageGet,
    mkStorageRemove,
    mkStorageSet,
} from "@ps-bridge/capabilities";
import {
    ioEffect,
    taskChain,
    taskDelay,
    taskEitherChain,
    taskEitherFold,
    taskEitherFromEffect,
    taskEitherLeft,
    taskEitherOf,
    taskFromIO,
    taskOf,
} from '@utils/effects/branded';
import * as IO from "@ps-bridge/interop/io";
import * as IntMath from "@ps-bridge/data/int";
import { renderRoute, routeLogin } from "@navigation/typedRoutes";
import { ok, type Ok } from "@ps-bridge/ok";

import { useTaskEffect, useTaskOnInit } from "@hooks/effects/useTaskEffect";
import { requireContext } from "@contexts/requireContext";
import { nowEpochSeconds, normalizeStoredRefreshTimestampSeconds } from "@contexts/jwtRefreshTiming";

// Context type with TaskEither for refreshing the token
interface JwtContextType {
    isTokenExpiring: StateHandle<boolean>;
    subscriptionStatus: StateHandle<JwtSubscriptionStatus>;
    hasCheckedAuth: StateRead<boolean>;
    controller: JwtSessionController;
}

export type JwtKernelOutput = {
    subscriptionStatus: StateRead<JwtSubscriptionStatus>;
    setSubscriptionStatus: StateUpdate<JwtSubscriptionStatus>;
};

const loginRouteWithCurrentPath = (): string => {
    const loginPath = renderRoute(routeLogin);
    if (typeof window === "undefined") {
        return loginPath;
    }
    const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return `${loginPath}?next=${encodeURIComponent(nextPath)}`;
};

const isLoginPath = (pathname: string): boolean => pathname === renderRoute(routeLogin);

const JwtContext = createContext<OptionTs<JwtContextType>>(Option.none());

export const JwtProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const isTokenExpiringHandle = useIoState(true);
    const { set: setIsTokenExpiringIo } = isTokenExpiringHandle;
    const { read: hasCheckedAuth, set: setHasCheckedAuthIo } = useIoState(false);
    const refreshDisabledKey = "jwtRefreshDisabled";
    const initialRefreshDisabled =
        typeof window !== "undefined" &&
        !!window.localStorage &&
        window.localStorage.getItem(refreshDisabledKey) === "true";
    const { read: refreshDisabled, set: setRefreshDisabledIo } = useIoState(initialRefreshDisabled);
    const subscriptionStatusHandle = useIoState<JwtSubscriptionStatus>(makeJwtSubscriptionStatus('not-found'));
    const { read: subscriptionStatus, set: setSubscriptionStatusIo } = subscriptionStatusHandle;
    const lastRefreshAtState = useIoOptionState<Int>(Option.none());
    const authenticationClient = useAuthenticationClient();
    const storageKey = "jwtLastRefreshAt";
    const refreshWindowSeconds = 24 * 60 * 60;
    const refreshCooldownSeconds = 60;
    const refreshWindowSecondsInt = IntMath.round(refreshWindowSeconds) as Int;
    const refreshCooldownSecondsInt = IntMath.round(refreshCooldownSeconds) as Int;
    const { read: lastRefreshAtRead, setSome: setLastRefreshAtSome, setMaybe: setLastRefreshAtMaybe } =
        lastRefreshAtState;

    const refreshToken: TaskEitherTs<AuthenticationError, JwtResponse> = useMemo(
        () =>
            taskEitherChain(
                taskEitherFromEffect(authenticationClient.refreshJwtToken, (reason) => reason as AuthenticationError),
                (jwtStatusResult) =>
                    unwrapJwtStatus(jwtStatusResult.status) === "unauthorized"
                        ? taskEitherLeft({
                              type: "Unauthorized",
                              message: makeErrorMessageText("Expired Authentication Token"),
                              statusCode: Option.none<HttpStatusCode>(),
                          })
                        : taskEitherOf(jwtStatusResult),
            ),
        [authenticationClient],
    );

    const handleRefreshSuccess = useCallback(
        (jwtStatus: JwtResponse): TaskTs<Ok> =>
            taskChain(
                taskFromIO(
                    (() => {
                        const nowInt = IntMath.round(nowEpochSeconds()) as Int;
                        return IO.sequenceOk([
                            setLastRefreshAtSome(nowInt),
                            ioEffect(() => {
                                if (typeof window !== "undefined" && window.localStorage) {
                                    const storedValue = IntMath.toNumber(nowInt) as number;
                                    window.localStorage.setItem(storageKey, String(storedValue));
                                    window.localStorage.removeItem(refreshDisabledKey);
                                }
                                return ok;
                            }),
                        ]);
                    })(),
                ),
                () =>
                    taskChain(
                        taskFromIO(setRefreshDisabledIo(false)),
                        () =>
                            taskChain(
                                taskFromIO(setIsTokenExpiringIo(false)),
                                () => taskFromIO(setSubscriptionStatusIo(jwtStatus.subscription_status)),
                            ),
                    ),
            ),
        [
            setLastRefreshAtSome,
            refreshDisabledKey,
            setIsTokenExpiringIo,
            setRefreshDisabledIo,
            setSubscriptionStatusIo,
            storageKey,
        ],
    );

    const refreshDecision = useCallback((): "skip" | "expired" | "run" => {
        if (refreshDisabled) {
            return "skip";
        }
        const inMemoryDecision = Option.fold(
            lastRefreshAtRead,
            () => undefined,
            (lastRefreshTime) =>
                nowEpochSeconds() - (IntMath.toNumber(lastRefreshTime) as number) < refreshCooldownSeconds
                    ? "skip"
                    : "run",
        );
        if (inMemoryDecision !== undefined) {
            return inMemoryDecision;
        }
        if (typeof window === "undefined" || !window.localStorage) {
            return "run";
        }
        const stored = window.localStorage.getItem(storageKey);
        if (!stored) {
            return "run";
        }
        const lastRefreshAt = normalizeStoredRefreshTimestampSeconds(stored);
        return Option.fold(
            lastRefreshAt,
            () => "run",
            (value) => (nowEpochSeconds() - value > refreshWindowSeconds ? "expired" : "run"),
        );
    }, [lastRefreshAtRead, refreshCooldownSeconds, refreshDisabled, refreshWindowSeconds]);

    const shouldDisableForError = useCallback((error: AuthenticationError) => {
        if (error.type === "Unauthorized" || error.type === "Forbidden") {
            return true;
        }
        return Option.fold(error.statusCode, () => false, (code) => code === 401 || code === 403);
    }, []);

    const clearSession: IOts<Ok> = useMemo(
        () =>
            IO.sequenceOk([
                setIsTokenExpiringIo(true),
                setSubscriptionStatusIo(makeJwtSubscriptionStatus("not-found")),
                setLastRefreshAtMaybe(Option.none()),
                ioEffect(() => {
                    if (typeof window !== "undefined" && window.localStorage) {
                        window.localStorage.removeItem(storageKey);
                    }
                    return ok;
                }),
            ]),
        [setLastRefreshAtMaybe, setIsTokenExpiringIo, setSubscriptionStatusIo, storageKey]
    );

    const handleRefreshError = useCallback(
        (error: AuthenticationError): TaskTs<Ok> =>
            taskChain(
                taskFromIO(
                    ioEffect(() => {
                        console.error("Error refreshing JWT:", error.message);
                        if (shouldDisableForError(error) && typeof window !== "undefined") {
                            if (window.localStorage) {
                                window.localStorage.setItem(refreshDisabledKey, "true");
                            }
                            // Avoid clobbering an existing login `next` chain with nested `/login?next=/login?...`.
                            if (!isLoginPath(window.location.pathname)) {
                                window.location.assign(loginRouteWithCurrentPath());
                            }
                        }
                        return ok;
                    }),
                ),
                () =>
                    taskChain(
                        taskFromIO(setIsTokenExpiringIo(true)),
                        () =>
                            shouldDisableForError(error)
                                ? taskChain(
                                      taskFromIO(setRefreshDisabledIo(true)),
                                      () => taskFromIO(clearSession),
                                  )
                                : taskOf(ok),
                    ),
            ),
        [clearSession, setIsTokenExpiringIo, setRefreshDisabledIo, shouldDisableForError],
    );

    const refreshIfNeededTask = useMemo(
        () =>
            taskChain(
                taskFromIO(
                    ioEffect(() => {
                        const decision = refreshDecision();
                        if (decision === "skip") {
                            console.log("Skipping token refresh; already refreshed within the last minute.");
                        }
                        return decision;
                    }),
                ),
                (decision) =>
                    decision === "skip"
                        ? taskOf(ok)
                        : decision === "expired"
                            ? taskChain(
                                  taskFromIO(
                                      ioEffect(() => {
                                          if (typeof window !== "undefined" && window.localStorage) {
                                              window.localStorage.setItem(refreshDisabledKey, "true");
                                          }
                                          return ok;
                                      }),
                                  ),
                                  () =>
                                      taskChain(
                                          taskFromIO(setRefreshDisabledIo(true)),
                                          () => taskFromIO(clearSession),
                                      ),
                              )
                            : taskEitherFold(refreshToken, handleRefreshError, handleRefreshSuccess),
            ),
        [clearSession, handleRefreshError, handleRefreshSuccess, refreshDecision, refreshToken, setRefreshDisabledIo],
    );

    const scheduleTokenRefreshTask = useCallback((): TaskTs<Ok> => {
        const scheduleNext = (): TaskTs<Ok> =>
            taskChain(taskDelay(90 * 60 * 1000, refreshIfNeededTask), () => scheduleNext());
        return scheduleNext();
    }, [refreshIfNeededTask]);

    const initialRefreshTask = useMemo(
        () =>
            taskChain(
                refreshIfNeededTask,
                () =>
                    taskChain(
                        taskFromIO(setHasCheckedAuthIo(true)),
                        () =>
                            refreshDisabled
                                ? taskFromIO(setSubscriptionStatusIo(makeJwtSubscriptionStatus("not-found")))
                                : taskOf(ok),
                    ),
            ),
        [refreshDisabled, refreshIfNeededTask, setHasCheckedAuthIo, setSubscriptionStatusIo],
    );

    const scheduledRefreshTask = useMemo(
        () => scheduleTokenRefreshTask(),
        [scheduleTokenRefreshTask],
    );

    useTaskOnInit(Option.some(initialRefreshTask));
    useTaskEffect(scheduledRefreshTask);

    const controller: JwtSessionController = useMemo(
        () =>
            mkJwtSessionController({
                subscriptionStatus: { read: subscriptionStatus, set: setSubscriptionStatusIo },
                refreshDisabled: { read: refreshDisabled, set: setRefreshDisabledIo },
                lastRefreshAt: {
                    read: lastRefreshAtRead,
                    set: setLastRefreshAtMaybe,
                },
                clockNow: mkClockNow(ioEffect(() => IntMath.round(Date.now()) as Int)),
                storageGet: mkStorageGet((key) =>
                    ioEffect(() => {
                        if (typeof window === "undefined" || !window.localStorage) {
                            return Option.none();
                        }
                        const stored = window.localStorage.getItem(key);
                        return typeof stored === "string" ? Option.some(stored) : Option.none();
                    }),
                ),
                storageSet: mkStorageSet(({ key, value }) =>
                    ioEffect(() => {
                        if (typeof window !== "undefined" && window.localStorage) {
                            window.localStorage.setItem(key, value);
                        }
                        return ok;
                    }),
                ),
                storageRemove: mkStorageRemove((key) =>
                    ioEffect(() => {
                        if (typeof window !== "undefined" && window.localStorage) {
                            window.localStorage.removeItem(key);
                        }
                        return ok;
                    }),
                ),
                navigate: mkNavigate((route) =>
                    ioEffect(() => {
                        if (typeof window !== "undefined") {
                            window.location.assign(renderRoute(route));
                        }
                        return ok;
                    }),
                ),
                refreshIntent: mkJwtRefreshIntent(refreshToken),
                refreshDisabledKey,
                refreshTimestampKey: storageKey,
                refreshCooldownMs: refreshCooldownSecondsInt,
                refreshWindowMs: refreshWindowSecondsInt,
                loginRoute: routeLogin,
            }),
        [
            refreshDisabled,
            refreshDisabledKey,
            refreshToken,
            refreshCooldownSecondsInt,
            refreshWindowSecondsInt,
            setRefreshDisabledIo,
            setSubscriptionStatusIo,
            storageKey,
            subscriptionStatus,
            lastRefreshAtRead,
            setLastRefreshAtMaybe,
        ],
    );

    const contextValue = {
        isTokenExpiring: isTokenExpiringHandle,
        subscriptionStatus: subscriptionStatusHandle,
        hasCheckedAuth,
        controller,
    };

    return <JwtContext.Provider value={Option.some(contextValue)}>{children}</JwtContext.Provider>;
};

const useJwtContext = (): JwtContextType => {
    return requireContext(
        useContext(JwtContext),
        'useJwtController/useJwtStateHandles must be used within a JwtProvider',
    );
};

export const useJwtKernel = (): JwtKernelOutput => {
    const { subscriptionStatus } = useJwtContext();
    return { subscriptionStatus: subscriptionStatus.read, setSubscriptionStatus: subscriptionStatus.set };
};

export const useJwtController = (): JwtSessionController => useJwtContext().controller;

export const useJwtStateHandles = (): {
    isTokenExpiring: StateHandle<boolean>;
    subscriptionStatus: StateHandle<JwtSubscriptionStatus>;
} => {
    const { isTokenExpiring, subscriptionStatus } = useJwtContext();
    return { isTokenExpiring, subscriptionStatus };
};

export const useJwtAuthRead = (): StateRead<boolean> => {
    const { hasCheckedAuth } = useJwtContext();
    return hasCheckedAuth;
};
